// Per-dialect synthesizer for CREATE / ALTER / DROP DATABASE (v2 Slice G1).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice G1.
//
// Per-engine surface:
//   - mssql:    CREATE DATABASE name [CONTAINMENT = …]
//                 [ON [PRIMARY] (NAME = …, FILENAME = …, SIZE = …, …) [, …]]
//                 [LOG ON (…) [, …]] [COLLATE name]
//               ALTER DATABASE name {MODIFY NAME = new |
//                 SET RECOVERY {FULL|SIMPLE|BULK_LOGGED} |
//                 SET COMPATIBILITY_LEVEL = n}
//               DROP DATABASE [IF EXISTS] name
//   - postgres: CREATE DATABASE name [WITH OWNER = … TEMPLATE = …
//                 ENCODING = '…' LC_COLLATE = '…' LC_CTYPE = '…'
//                 TABLESPACE = … CONNECTION LIMIT = n]
//               ALTER DATABASE name {RENAME TO new | OWNER TO r |
//                 SET param = value | RESET param}
//               DROP DATABASE [IF EXISTS] name [WITH (FORCE)]
//   - mysql:    CREATE {DATABASE|SCHEMA} [IF NOT EXISTS] name
//                 [CHARACTER SET = …] [COLLATE = …]
//               ALTER {DATABASE|SCHEMA} name
//                 {CHARACTER SET = … | COLLATE = …}
//               DROP {DATABASE|SCHEMA} [IF EXISTS] name
//   - oracle:   CREATE PLUGGABLE DATABASE name ADMIN USER u IDENTIFIED BY p
//                 [FILE_NAME_CONVERT = (…)]
//               ALTER PLUGGABLE DATABASE name {OPEN | CLOSE IMMEDIATE}
//               DROP PLUGGABLE DATABASE name [INCLUDING DATAFILES]
//               (CDB-level CREATE DATABASE is out of scope — RV2-7.)
//   - sqlite:   not supported (databases are filesystem files; use ATTACH).

import type { SqlEngine } from '../../../sql-grammar/model';
import type {
  DatabaseChange,
  DatabaseDef,
  DatabaseFileSpec,
} from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent, quoteSqlLiteral } from './dialect';

const SQLITE_HINT
  = `sqlite databases are filesystem files. Use ATTACH DATABASE '<path>' `
    + `AS <alias> from the connection layer to attach an additional database, `
    + `or open a connection to the file directly.`;

const ORACLE_CDB_NOTE
  = `Oracle CREATE DATABASE (CDB-level) is out of scope per PRD RV2-7 — use `
    + `oracleAdminUser + oracleAdminPassword to emit CREATE PLUGGABLE DATABASE.`;

function rejectIfSqlite(engine: SqlEngine, op: string): void {
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: ${engine} does not support ${op} — ${SQLITE_HINT}`,
    );
  }
}

function rejectField(engine: SqlEngine, op: string, field: string, allowed: string): never {
  throw new DdlEditError(
    'invalid-edit',
    `applyDdlEdit: ${engine} ${op} does not accept '${field}' — only ${allowed} accept this field.`,
  );
}

// ─── CREATE DATABASE ──────────────────────────────────────────────────────

export function synthesizeCreateDatabase(engine: SqlEngine, db: DatabaseDef): string {
  rejectIfSqlite(engine, 'create-database');
  if (engine === 'mssql') return synthMssqlCreateDatabase(db);
  if (engine === 'postgres') return synthPgsqlCreateDatabase(db);
  if (engine === 'mysql') return synthMysqlCreateDatabase(db);
  return synthOracleCreateDatabase(db);
}

function synthMssqlCreateDatabase(db: DatabaseDef): string {
  if (hasAnyPgsql(db)) rejectField('mssql', 'create-database', 'pgsql*', 'pgsql');
  if (hasAnyMysql(db)) rejectField('mssql', 'create-database', 'mysql*', 'mysql');
  if (hasAnyOracle(db)) rejectField('mssql', 'create-database', 'oracle*', 'oracle');
  if (db.ifNotExists) {
    rejectField('mssql', 'create-database', 'ifNotExists', 'pgsql / mysql');
  }
  const name = maybeQuoteIdent('mssql', db.name, db.quoted ?? false);
  let s = `CREATE DATABASE ${name}`;
  if (db.mssqlContainment !== undefined) {
    const c = db.mssqlContainment === 'partial' ? 'PARTIAL' : 'NONE';
    s += ` CONTAINMENT = ${c}`;
  }
  if (db.mssqlDataFiles !== undefined && db.mssqlDataFiles.length > 0) {
    const primary = db.mssqlOnPrimary ? 'PRIMARY ' : '';
    s += ` ON ${primary}${formatMssqlFiles(db.mssqlDataFiles)}`;
  }
  if (db.mssqlLogFiles !== undefined && db.mssqlLogFiles.length > 0) {
    s += ` LOG ON ${formatMssqlFiles(db.mssqlLogFiles)}`;
  }
  if (db.mssqlCollate !== undefined) s += ` COLLATE ${db.mssqlCollate}`;
  return s;
}

function formatMssqlFiles(files: readonly DatabaseFileSpec[]): string {
  return files.map(formatMssqlFile).join(', ');
}

function formatMssqlFile(f: DatabaseFileSpec): string {
  const parts: string[] = [`NAME = ${f.name}`, `FILENAME = ${quoteSqlLiteral(f.filename)}`];
  if (f.size !== undefined) parts.push(`SIZE = ${f.size}`);
  if (f.maxsize !== undefined) parts.push(`MAXSIZE = ${f.maxsize}`);
  if (f.filegrowth !== undefined) parts.push(`FILEGROWTH = ${f.filegrowth}`);
  return `(${parts.join(', ')})`;
}

function synthPgsqlCreateDatabase(db: DatabaseDef): string {
  if (hasAnyMssql(db)) rejectField('postgres', 'create-database', 'mssql*', 'mssql');
  if (hasAnyMysql(db)) rejectField('postgres', 'create-database', 'mysql*', 'mysql');
  if (hasAnyOracle(db)) rejectField('postgres', 'create-database', 'oracle*', 'oracle');
  // PostgreSQL's CREATE DATABASE grammar has NO `IF NOT EXISTS` clause (unlike
  // MySQL). Emitting it is a syntax error, but throwing would break the VDE
  // create-database form (which may set the flag), so we silently OMIT the
  // clause and emit a valid `CREATE DATABASE <name>` instead. (F08)
  const name = maybeQuoteIdent('postgres', db.name, db.quoted ?? false);
  let s = `CREATE DATABASE ${name}`;
  const opts = pgsqlCreateOpts(db);
  if (opts.length > 0) s += ` WITH ${opts.join(' ')}`;
  return s;
}

function pgsqlCreateOpts(db: DatabaseDef): string[] {
  const out: string[] = [];
  if (db.pgsqlOwner !== undefined) out.push(`OWNER = ${db.pgsqlOwner}`);
  if (db.pgsqlTemplate !== undefined) out.push(`TEMPLATE = ${db.pgsqlTemplate}`);
  if (db.pgsqlEncoding !== undefined) out.push(`ENCODING = ${quoteSqlLiteral(db.pgsqlEncoding)}`);
  if (db.pgsqlLcCollate !== undefined) out.push(`LC_COLLATE = ${quoteSqlLiteral(db.pgsqlLcCollate)}`);
  if (db.pgsqlLcCtype !== undefined) out.push(`LC_CTYPE = ${quoteSqlLiteral(db.pgsqlLcCtype)}`);
  if (db.pgsqlTablespace !== undefined) out.push(`TABLESPACE = ${db.pgsqlTablespace}`);
  if (db.pgsqlConnectionLimit !== undefined) {
    out.push(`CONNECTION LIMIT = ${db.pgsqlConnectionLimit}`);
  }
  return out;
}

function synthMysqlCreateDatabase(db: DatabaseDef): string {
  if (hasAnyMssql(db)) rejectField('mysql', 'create-database', 'mssql*', 'mssql');
  if (hasAnyPgsql(db)) rejectField('mysql', 'create-database', 'pgsql*', 'pgsql');
  if (hasAnyOracle(db)) rejectField('mysql', 'create-database', 'oracle*', 'oracle');
  const keyword = db.mysqlSchema ? 'SCHEMA' : 'DATABASE';
  const name = maybeQuoteIdent('mysql', db.name, db.quoted ?? false);
  let s = `CREATE ${keyword}`;
  if (db.ifNotExists) s += ' IF NOT EXISTS';
  s += ` ${name}`;
  if (db.mysqlCharacterSet !== undefined) s += ` CHARACTER SET = ${db.mysqlCharacterSet}`;
  if (db.mysqlCollate !== undefined) s += ` COLLATE = ${db.mysqlCollate}`;
  return s;
}

function synthOracleCreateDatabase(db: DatabaseDef): string {
  if (hasAnyMssql(db)) rejectField('oracle', 'create-database', 'mssql*', 'mssql');
  if (hasAnyPgsql(db)) rejectField('oracle', 'create-database', 'pgsql*', 'pgsql');
  if (hasAnyMysql(db)) rejectField('oracle', 'create-database', 'mysql*', 'mysql');
  if (db.ifNotExists) {
    rejectField('oracle', 'create-database', 'ifNotExists', 'pgsql / mysql');
  }
  if (db.oracleAdminUser === undefined || db.oracleAdminPassword === undefined) {
    throw new DdlEditError(
      'invalid-edit',
      `applyDdlEdit: oracle create-database requires both 'oracleAdminUser' `
        + `and 'oracleAdminPassword' (PLUGGABLE DATABASE admin credentials). `
        + ORACLE_CDB_NOTE,
    );
  }
  const name = maybeQuoteIdent('oracle', db.name, db.quoted ?? false);
  let s = `CREATE PLUGGABLE DATABASE ${name} ADMIN USER ${db.oracleAdminUser}`;
  s += ` IDENTIFIED BY ${db.oracleAdminPassword}`;
  if (db.oracleFileNameConvertText !== undefined) {
    s += ` FILE_NAME_CONVERT = ${db.oracleFileNameConvertText}`;
  }
  return s;
}

// ─── ALTER DATABASE ───────────────────────────────────────────────────────

export function synthesizeAlterDatabase(
  engine: SqlEngine,
  databaseName: string,
  quoted: boolean,
  mysqlSchema: boolean | undefined,
  change: DatabaseChange,
): string {
  rejectIfSqlite(engine, 'alter-database');
  if (changeIsEmpty(change)) {
    throw new DdlEditError(
      'invalid-edit',
      `applyDdlEdit: alter-database requires at least one change field.`,
    );
  }
  if (engine === 'mssql') return synthMssqlAlterDatabase(databaseName, quoted, change);
  if (engine === 'postgres') return synthPgsqlAlterDatabase(databaseName, quoted, change);
  if (engine === 'mysql') return synthMysqlAlterDatabase(databaseName, quoted, mysqlSchema, change);
  return synthOracleAlterDatabase(databaseName, quoted, change);
}

function changeIsEmpty(c: DatabaseChange): boolean {
  return c.mssqlNewName === undefined && c.mssqlRecovery === undefined
    && c.mssqlCompatibilityLevel === undefined && c.pgsqlRenameTo === undefined
    && c.pgsqlOwnerTo === undefined && (c.pgsqlSetOptions === undefined || c.pgsqlSetOptions.length === 0)
    && (c.pgsqlResetOptions === undefined || c.pgsqlResetOptions.length === 0)
    && c.mysqlCharacterSet === undefined && c.mysqlCollate === undefined
    && c.oraclePdbOpen === undefined;
}

function synthMssqlAlterDatabase(databaseName: string, quoted: boolean, change: DatabaseChange): string {
  if (change.pgsqlRenameTo !== undefined || change.pgsqlOwnerTo !== undefined
      || change.pgsqlSetOptions !== undefined || change.pgsqlResetOptions !== undefined) {
    rejectField('mssql', 'alter-database', 'pgsql*', 'pgsql');
  }
  if (change.mysqlCharacterSet !== undefined || change.mysqlCollate !== undefined) {
    rejectField('mssql', 'alter-database', 'mysql*', 'mysql');
  }
  if (change.oraclePdbOpen !== undefined) {
    rejectField('mssql', 'alter-database', 'oracle*', 'oracle');
  }
  const name = maybeQuoteIdent('mssql', databaseName, quoted);
  const stmts: string[] = [];
  if (change.mssqlNewName !== undefined) {
    stmts.push(`ALTER DATABASE ${name} MODIFY NAME = ${change.mssqlNewName}`);
  }
  if (change.mssqlRecovery !== undefined) {
    const r = mssqlRecoveryKeyword(change.mssqlRecovery);
    stmts.push(`ALTER DATABASE ${name} SET RECOVERY ${r}`);
  }
  if (change.mssqlCompatibilityLevel !== undefined) {
    stmts.push(`ALTER DATABASE ${name} SET COMPATIBILITY_LEVEL = ${change.mssqlCompatibilityLevel}`);
  }
  return stmts.join(';\n');
}

function mssqlRecoveryKeyword(r: 'full' | 'simple' | 'bulk-logged'): string {
  if (r === 'full') return 'FULL';
  if (r === 'simple') return 'SIMPLE';
  return 'BULK_LOGGED';
}

function synthPgsqlAlterDatabase(databaseName: string, quoted: boolean, change: DatabaseChange): string {
  if (change.mssqlNewName !== undefined || change.mssqlRecovery !== undefined
      || change.mssqlCompatibilityLevel !== undefined) {
    rejectField('postgres', 'alter-database', 'mssql*', 'mssql');
  }
  if (change.mysqlCharacterSet !== undefined || change.mysqlCollate !== undefined) {
    rejectField('postgres', 'alter-database', 'mysql*', 'mysql');
  }
  if (change.oraclePdbOpen !== undefined) {
    rejectField('postgres', 'alter-database', 'oracle*', 'oracle');
  }
  const name = maybeQuoteIdent('postgres', databaseName, quoted);
  const stmts: string[] = [];
  if (change.pgsqlRenameTo !== undefined) {
    stmts.push(`ALTER DATABASE ${name} RENAME TO ${change.pgsqlRenameTo}`);
  }
  if (change.pgsqlOwnerTo !== undefined) {
    stmts.push(`ALTER DATABASE ${name} OWNER TO ${change.pgsqlOwnerTo}`);
  }
  if (change.pgsqlSetOptions !== undefined) {
    for (const opt of change.pgsqlSetOptions) {
      stmts.push(`ALTER DATABASE ${name} SET ${opt.key} = ${opt.value}`);
    }
  }
  if (change.pgsqlResetOptions !== undefined) {
    for (const key of change.pgsqlResetOptions) {
      stmts.push(`ALTER DATABASE ${name} RESET ${key}`);
    }
  }
  return stmts.join(';\n');
}

function synthMysqlAlterDatabase(
  databaseName: string,
  quoted: boolean,
  mysqlSchema: boolean | undefined,
  change: DatabaseChange,
): string {
  if (change.mssqlNewName !== undefined || change.mssqlRecovery !== undefined
      || change.mssqlCompatibilityLevel !== undefined) {
    rejectField('mysql', 'alter-database', 'mssql*', 'mssql');
  }
  if (change.pgsqlRenameTo !== undefined || change.pgsqlOwnerTo !== undefined
      || change.pgsqlSetOptions !== undefined || change.pgsqlResetOptions !== undefined) {
    rejectField('mysql', 'alter-database', 'pgsql*', 'pgsql');
  }
  if (change.oraclePdbOpen !== undefined) {
    rejectField('mysql', 'alter-database', 'oracle*', 'oracle');
  }
  const keyword = mysqlSchema ? 'SCHEMA' : 'DATABASE';
  const name = maybeQuoteIdent('mysql', databaseName, quoted);
  const parts: string[] = [];
  if (change.mysqlCharacterSet !== undefined) parts.push(`CHARACTER SET = ${change.mysqlCharacterSet}`);
  if (change.mysqlCollate !== undefined) parts.push(`COLLATE = ${change.mysqlCollate}`);
  return `ALTER ${keyword} ${name} ${parts.join(' ')}`;
}

function synthOracleAlterDatabase(databaseName: string, quoted: boolean, change: DatabaseChange): string {
  if (change.mssqlNewName !== undefined || change.mssqlRecovery !== undefined
      || change.mssqlCompatibilityLevel !== undefined) {
    rejectField('oracle', 'alter-database', 'mssql*', 'mssql');
  }
  if (change.pgsqlRenameTo !== undefined || change.pgsqlOwnerTo !== undefined
      || change.pgsqlSetOptions !== undefined || change.pgsqlResetOptions !== undefined) {
    rejectField('oracle', 'alter-database', 'pgsql*', 'pgsql');
  }
  if (change.mysqlCharacterSet !== undefined || change.mysqlCollate !== undefined) {
    rejectField('oracle', 'alter-database', 'mysql*', 'mysql');
  }
  const name = maybeQuoteIdent('oracle', databaseName, quoted);
  const action = change.oraclePdbOpen ? 'OPEN' : 'CLOSE IMMEDIATE';
  return `ALTER PLUGGABLE DATABASE ${name} ${action}`;
}

// ─── DROP DATABASE ────────────────────────────────────────────────────────

export interface DropDatabaseOptions {
  readonly databaseName: string;
  readonly quoted: boolean;
  readonly ifExists: boolean;
  readonly mysqlSchema?: boolean;
  readonly pgsqlForce?: boolean;
  readonly oraclePdbOptionsText?: string;
}

export function synthesizeDropDatabase(engine: SqlEngine, opts: DropDatabaseOptions): string {
  rejectIfSqlite(engine, 'drop-database');
  if (engine === 'mssql') return synthMssqlDropDatabase(opts);
  if (engine === 'postgres') return synthPgsqlDropDatabase(opts);
  if (engine === 'mysql') return synthMysqlDropDatabase(opts);
  return synthOracleDropDatabase(opts);
}

function synthMssqlDropDatabase(opts: DropDatabaseOptions): string {
  if (opts.mysqlSchema) {
    rejectField('mssql', 'drop-database', 'mysqlSchema', 'mysql');
  }
  if (opts.pgsqlForce) {
    rejectField('mssql', 'drop-database', 'pgsqlForce', 'pgsql');
  }
  if (opts.oraclePdbOptionsText !== undefined) {
    rejectField('mssql', 'drop-database', 'oraclePdbOptionsText', 'oracle');
  }
  const name = maybeQuoteIdent('mssql', opts.databaseName, opts.quoted);
  let s = 'DROP DATABASE';
  if (opts.ifExists) s += ' IF EXISTS';
  s += ` ${name}`;
  return s;
}

function synthPgsqlDropDatabase(opts: DropDatabaseOptions): string {
  if (opts.mysqlSchema) {
    rejectField('postgres', 'drop-database', 'mysqlSchema', 'mysql');
  }
  if (opts.oraclePdbOptionsText !== undefined) {
    rejectField('postgres', 'drop-database', 'oraclePdbOptionsText', 'oracle');
  }
  const name = maybeQuoteIdent('postgres', opts.databaseName, opts.quoted);
  let s = 'DROP DATABASE';
  if (opts.ifExists) s += ' IF EXISTS';
  s += ` ${name}`;
  if (opts.pgsqlForce) s += ' WITH (FORCE)';
  return s;
}

function synthMysqlDropDatabase(opts: DropDatabaseOptions): string {
  if (opts.pgsqlForce) {
    rejectField('mysql', 'drop-database', 'pgsqlForce', 'pgsql');
  }
  if (opts.oraclePdbOptionsText !== undefined) {
    rejectField('mysql', 'drop-database', 'oraclePdbOptionsText', 'oracle');
  }
  const keyword = opts.mysqlSchema ? 'SCHEMA' : 'DATABASE';
  const name = maybeQuoteIdent('mysql', opts.databaseName, opts.quoted);
  let s = `DROP ${keyword}`;
  if (opts.ifExists) s += ' IF EXISTS';
  s += ` ${name}`;
  return s;
}

function synthOracleDropDatabase(opts: DropDatabaseOptions): string {
  if (opts.mysqlSchema) {
    rejectField('oracle', 'drop-database', 'mysqlSchema', 'mysql');
  }
  if (opts.pgsqlForce) {
    rejectField('oracle', 'drop-database', 'pgsqlForce', 'pgsql');
  }
  if (opts.ifExists) {
    rejectField('oracle', 'drop-database', 'ifExists', 'mssql / pgsql / mysql');
  }
  const name = maybeQuoteIdent('oracle', opts.databaseName, opts.quoted);
  let s = `DROP PLUGGABLE DATABASE ${name}`;
  if (opts.oraclePdbOptionsText !== undefined) s += ` ${opts.oraclePdbOptionsText}`;
  return s;
}

// ─── per-engine field-presence helpers ────────────────────────────────────

function hasAnyMssql(db: DatabaseDef): boolean {
  return db.mssqlContainment !== undefined
    || db.mssqlDataFiles !== undefined
    || db.mssqlOnPrimary !== undefined
    || db.mssqlLogFiles !== undefined
    || db.mssqlCollate !== undefined;
}

function hasAnyPgsql(db: DatabaseDef): boolean {
  return db.pgsqlOwner !== undefined || db.pgsqlTemplate !== undefined
    || db.pgsqlEncoding !== undefined || db.pgsqlLcCollate !== undefined
    || db.pgsqlLcCtype !== undefined || db.pgsqlTablespace !== undefined
    || db.pgsqlConnectionLimit !== undefined;
}

function hasAnyMysql(db: DatabaseDef): boolean {
  return db.mysqlSchema !== undefined
    || db.mysqlCharacterSet !== undefined
    || db.mysqlCollate !== undefined;
}

function hasAnyOracle(db: DatabaseDef): boolean {
  return db.oracleAdminUser !== undefined
    || db.oracleAdminPassword !== undefined
    || db.oracleFileNameConvertText !== undefined;
}
